/**
 * Performance Monitoring Utilities
 *
 * Tools for tracking and logging performance metrics in the Forge app.
 * Uses Performance API for accurate measurements.
 *
 * Features:
 * - Performance marks and measures
 * - Slow query detection
 * - Component render tracking
 * - Page load metrics
 * - Long task detection
 *
 * @example
 * ```tsx
 * // Track a specific operation
 * const perf = startPerformanceMark('load-work-hub');
 * await fetchWorkItems();
 * perf.end(); // Logs if > threshold
 *
 * // Track component renders
 * function MyComponent() {
 *   usePerformanceTracking('MyComponent');
 *   return <div>...</div>;
 * }
 *
 * // Track queries
 * const { data } = useQuery({
 *   queryKey: ['tasks'],
 *   queryFn: trackQueryPerformance('tasks', fetchTasks),
 * });
 * ```
 */

import { useEffect, useRef } from 'react';

/**
 * Configuration
 */
const PERFORMANCE_CONFIG = {
  /** Threshold for slow operations (ms) */
  slowThreshold: 500,

  /** Threshold for very slow operations (ms) */
  verySlowThreshold: 1000,

  /** Threshold for logging long tasks (ms). Tasks between the browser's 50ms
   *  longtask threshold and this value are silently ignored to reduce noise. */
  longTaskLogThreshold: 100,

  /** Enable performance logging in development */
  enableLogging: import.meta.env.DEV,

  /** Enable performance logging in production */
  enableProductionLogging: import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === "true",

  /** Log to console */
  logToConsole: true,

  /** Send to analytics (e.g., telemetry) */
  sendToAnalytics: Boolean(import.meta.env.VITE_PERFORMANCE_METRICS_ENDPOINT),
  telemetryEndpoint: import.meta.env.VITE_PERFORMANCE_METRICS_ENDPOINT || "",
};

export interface InitPerformanceMonitoringOptions {
  /** Enable production logging at runtime, regardless of env defaults. */
  enableProductionLogging?: boolean;
}

/**
 * Performance mark result
 */
interface PerformanceMark {
  name: string;
  startTime: number;
  end: () => void;
}

/**
 * Start a performance mark
 */
export function startPerformanceMark(name: string): PerformanceMark {
  const startTime = performance.now();
  const markName = `forge:${name}`;

  if (performance.mark) {
    performance.mark(`${markName}:start`);
  }

  return {
    name,
    startTime,
    end: () => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (performance.mark && performance.measure) {
        performance.mark(`${markName}:end`);
        performance.measure(markName, `${markName}:start`, `${markName}:end`);
      }

      logPerformance(name, duration);
    },
  };
}

/**
 * Log performance metrics
 */
function logPerformance(name: string, duration: number, metadata?: Record<string, unknown>) {
  const shouldLog =
    (PERFORMANCE_CONFIG.enableLogging && import.meta.env.DEV) ||
    (PERFORMANCE_CONFIG.enableProductionLogging && !import.meta.env.DEV);

  if (!shouldLog) return;

  const isSlow = duration > PERFORMANCE_CONFIG.slowThreshold;
  const isVerySlow = duration > PERFORMANCE_CONFIG.verySlowThreshold;

  // Only log slow operations to reduce noise
  if (!isSlow) return;

  const level = isVerySlow ? 'warn' : 'log';
  const emoji = isVerySlow ? '🐌' : '⏱️';

  if (PERFORMANCE_CONFIG.logToConsole) {
    console[level](
      `${emoji} Performance [${name}]: ${duration.toFixed(2)}ms`,
      metadata || ''
    );
  }

  if (PERFORMANCE_CONFIG.sendToAnalytics) {
    sendPerformanceMetric({
      name,
      duration,
      isSlow,
      isVerySlow,
      ...metadata,
    });
  }
}

/**
 * Send performance metric to analytics
 */
function sendPerformanceMetric(metric: Record<string, unknown>) {
  if (!PERFORMANCE_CONFIG.sendToAnalytics || !PERFORMANCE_CONFIG.telemetryEndpoint) {
    return;
  }

  if (typeof performance === "undefined") return;

  if (typeof window === "undefined") return;

  const endpoint = PERFORMANCE_CONFIG.telemetryEndpoint.trim();
  if (!endpoint) return;

  const payload = {
    ...metric,
    measured_at: new Date().toISOString(),
    path: window.location.pathname,
    route: window.location.href,
    platform: "web",
  };

  try {
    const body = JSON.stringify(payload);

    if ("sendBeacon" in navigator) {
      const didQueue = navigator.sendBeacon(
        endpoint,
        new Blob([body], { type: "application/json" }),
      );
      if (didQueue) return;
    }

    void fetch(PERFORMANCE_CONFIG.telemetryEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).then((response) => {
      if (!response.ok && import.meta.env.DEV) {
        console.debug("Performance metric endpoint returned:", response.status);
      }
    }).catch((error: unknown) => {
      if (import.meta.env.DEV) {
        console.debug("Performance metric failed to post:", error);
      }
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.debug("Performance metric failed to send:", error);
    }
  }
}

/**
 * Hook to track component render performance
 */
export function usePerformanceTracking(componentName: string) {
  const renderCount = useRef(0);
  const mountTime = useRef<number>(0);

  useEffect(() => {
    renderCount.current += 1;

    if (renderCount.current === 1) {
      // First render (mount)
      mountTime.current = performance.now();
    }
  });

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      const totalTime = performance.now() - mountTime.current;
      if (renderCount.current > 10) {
        console.warn(
          `⚠️ Component [${componentName}] rendered ${renderCount.current} times in ${totalTime.toFixed(2)}ms`
        );
      }
    };
  }, [componentName]);
}

/**
 * Wrapper for query functions to track performance
 */
export function trackQueryPerformance<T>(
  queryName: string,
  queryFn: () => Promise<T>
): () => Promise<T> {
  return async () => {
    const mark = startPerformanceMark(`query:${queryName}`);
    try {
      const result = await queryFn();
      mark.end();
      return result;
    } catch (error) {
      mark.end();
      throw error;
    }
  };
}

/**
 * Track long tasks using PerformanceObserver
 */
export function observeLongTasks() {
  if (!('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const isBrowserLongTask = entry.entryType === 'longtask' && entry.duration > 50;
        const isSlowMeasure = entry.entryType === 'measure' && entry.duration > PERFORMANCE_CONFIG.slowThreshold;

        if (isBrowserLongTask) {
          // Only log long tasks that exceed our configurable threshold (default 100ms)
          // to avoid noise from tasks barely over the browser's 50ms longtask floor.
          if (entry.duration >= PERFORMANCE_CONFIG.longTaskLogThreshold) {
            console.warn(
              `🐌 Long Task Detected: ${entry.name} took ${entry.duration.toFixed(2)}ms`
            );
          }

          sendPerformanceMetric({
            type: 'long-task',
            name: entry.name,
            duration: entry.duration,
          });
          continue;
        }

        if (isSlowMeasure) {
          // App performance measures use the standard slow threshold (500ms by default)
          logPerformance(entry.name, entry.duration, { type: 'measure' });
        }
      }
    });

    observer.observe({ entryTypes: ['longtask', 'measure'] });

    return () => observer.disconnect();
  } catch (error) {
    console.debug('PerformanceObserver not supported:', error);
  }
}

/**
 * Measure page load performance
 */
export function measurePageLoad() {
  if (typeof window === 'undefined' || !window.performance) return;

  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (!navigation) return;

      const metrics = {
        // Time to first byte
        ttfb: navigation.responseStart - navigation.requestStart,

        // DOM processing time
        domProcessing: navigation.domComplete - navigation.domInteractive,

        // Total load time
        loadTime: navigation.loadEventEnd - navigation.fetchStart,

        // DOM content loaded
        domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
      };

      if (import.meta.env.DEV) {
        console.log('Page Load Metrics:', {
          TTFB: `${metrics.ttfb.toFixed(2)}ms`,
          'DOM Processing': `${metrics.domProcessing.toFixed(2)}ms`,
          'DOM Content Loaded': `${metrics.domContentLoaded.toFixed(2)}ms`,
          'Total Load': `${metrics.loadTime.toFixed(2)}ms`,
        });
      }

      sendPerformanceMetric({
        type: 'page-load',
        ...metrics,
      });
    }, 0);
  });
}

/**
 * Measure Web Vitals (Core Web Vitals)
 */
export function measureWebVitals() {
  if (!('PerformanceObserver' in window)) return;

  // Largest Contentful Paint (LCP)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number };

      if (import.meta.env.DEV) console.log(`LCP: ${(lastEntry.renderTime ?? lastEntry.startTime).toFixed(2)}ms`);
      sendPerformanceMetric({
        type: 'web-vital',
        metric: 'LCP',
        value: lastEntry.renderTime ?? lastEntry.startTime,
      });
    });

    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
  } catch (error) {
    console.debug('LCP observation not supported');
  }

  // First Input Delay (FID)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEntry & { processingStart?: number };
        const fid = (fidEntry.processingStart ?? entry.startTime) - entry.startTime;
        if (import.meta.env.DEV) console.log(`FID: ${fid.toFixed(2)}ms`);
        sendPerformanceMetric({
          type: 'web-vital',
          metric: 'FID',
          value: fid,
        });
      });
    });

    fidObserver.observe({ entryTypes: ['first-input'] });
  } catch (error) {
    console.debug('FID observation not supported');
  }

  // Cumulative Layout Shift (CLS)
  try {
    let clsScore = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const layoutEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!layoutEntry.hadRecentInput) {
          clsScore += layoutEntry.value ?? 0;
        }
      });

      if (import.meta.env.DEV) console.log(`CLS: ${clsScore.toFixed(4)}`);
      sendPerformanceMetric({
        type: 'web-vital',
        metric: 'CLS',
        value: clsScore,
      });
    });

    clsObserver.observe({ entryTypes: ['layout-shift'] });
  } catch (error) {
    console.debug('CLS observation not supported');
  }
}

/**
 * Get performance summary for current page
 */
export function getPerformanceSummary() {
  if (!performance.getEntriesByType) return null;

  const measures = performance.getEntriesByType('measure');
  const marks = performance.getEntriesByType('mark');

  return {
    measures: measures.map((m) => ({
      name: m.name,
      duration: m.duration,
    })),
    marks: marks.map((m) => ({
      name: m.name,
      startTime: m.startTime,
    })),
  };
}

/**
 * Clear all performance marks and measures
 */
export function clearPerformanceData() {
  if (performance.clearMarks) {
    performance.clearMarks();
  }
  if (performance.clearMeasures) {
    performance.clearMeasures();
  }
}

/**
 * Initialize performance monitoring
 * Call this once in your app entry point
 */
export function initPerformanceMonitoring(options: InitPerformanceMonitoringOptions = {}) {
  if (typeof window === 'undefined') return;

  if (typeof options.enableProductionLogging === 'boolean') {
    PERFORMANCE_CONFIG.enableProductionLogging = options.enableProductionLogging;
  }

  measurePageLoad();
  measureWebVitals();
  const cleanup = observeLongTasks();

  return cleanup;
}
